import {
	BankAccountEntityType,
	ClientEntityType,
	GuarantorEntityType,
	JobEntityType,
	PatientEntityType,
} from "@/lib/supabase/supabaseTypes";
import {
	isBldgNbOrAdrLine2Valid,
	isCtryValid,
	isIBANValid,
	isNameValid,
	isPstCdValid,
	isRefValid,
	isStrtNmOrAdrLine1Valid,
	isTwnNmValid,
} from "./qr-payment-validation-functions";
import { generateQrReferenceNumber } from "@/dentlab/src/lib/utils/qr-reference-generator";
import { AddressRecipient } from "../../utils";
import { getCountryFromSwissPostalCode } from "@/dentlab/src/lib/utils/country-utils";

// Any 6-digit invoice number: the reference's validity only depends on the bank account's part.
const SAMPLE_INVOICE_NUMBER = "000000";

export type PaymentSlipRecipient =
	| ClientEntityType
	| GuarantorEntityType
	| PatientEntityType;

export type PaymentSlipDebtorKind = "patient" | "guarantor" | "client";

export interface PaymentSlipParties {
	debtor: PaymentSlipRecipient;
	debtorKind: PaymentSlipDebtorKind;
	/** Patients have no bank account, so the account is resolved via guarantor or client. */
	bankAccountOwner: ClientEntityType | GuarantorEntityType;
}

export const resolvePaymentSlipParties = ({
	job,
	client,
	guarantor,
	patient,
}: {
	job: Pick<JobEntityType, "guarantor_id" | "is_paid_by_patient">;
	client: ClientEntityType;
	guarantor?: GuarantorEntityType | null;
	patient?: PatientEntityType | null;
}): PaymentSlipParties => {
	const bankAccountOwner = job.guarantor_id && guarantor ? guarantor : client;
	if (job.is_paid_by_patient && patient) {
		return { debtor: patient, debtorKind: "patient", bankAccountOwner };
	}
	return {
		debtor: bankAccountOwner,
		debtorKind: bankAccountOwner === client ? "client" : "guarantor",
		bankAccountOwner,
	};
};

export interface PaymentSlipDebtorAddress {
	name: string;
	street: string;
	postalCode: string;
	city: string;
	country: string;
}

export const getPaymentSlipDebtorAddress = (
	recipient: AddressRecipient
): PaymentSlipDebtorAddress => {
	const client = recipient as ClientEntityType;
	const company = recipient as ClientEntityType | GuarantorEntityType;
	const useCorrespondence = !!client.is_correspondence_address;

	const isCompany = useCorrespondence
		? client.c_is_company
		: company.is_company;

	const companyName = (
		useCorrespondence ? client.c_company_name : company.company_name
	)?.trim();

	// A company flag without a company name falls back to the person, like the address block.
	let name: string;
	if (isCompany && companyName) {
		name = companyName;
	} else {
		const firstName = useCorrespondence
			? client.c_first_name || ""
			: recipient.first_name || "";
		const lastName = useCorrespondence
			? client.c_last_name || ""
			: recipient.last_name || "";
		name = `${firstName} ${lastName}`.trim();
	}

	const postalCode =
		(useCorrespondence ? client.c_postal_code : recipient.postal_code) || "";

	return {
		name,
		street: (useCorrespondence ? client.c_street : recipient.street) || "",
		postalCode,
		city: (useCorrespondence ? client.c_city : recipient.city) || "",
		country: (
			recipient.country || getCountryFromSwissPostalCode(postalCode)
		).toUpperCase(),
	};
};

export type PaymentSlipDebtorField =
	| "name"
	| "street"
	| "postal_code"
	| "city"
	| "country";

export interface PaymentSlipDebtorProblem {
	field: PaymentSlipDebtorField;
	message: string;
}

// Only the structured address type "S" is used for now.
const ADDRESS_TYPE = "S";

/** Every debtor (EZP_*) problem of the QR bill, in the order the QR slip reports them. */
export const validatePaymentSlipDebtor = (
	recipient: AddressRecipient
): PaymentSlipDebtorProblem[] => {
	const { name, street, postalCode, city, country } =
		getPaymentSlipDebtorAddress(recipient);
	const problems: PaymentSlipDebtorProblem[] = [];

	if (!isNameValid(name) || name.trim().length === 0) {
		problems.push({
			field: "name",
			message: `Der Name des Zahlungspflichtigen ist unzulässig oder leer (${name}). Bitte stellen Sie sicher, dass der Empfänger einen gültigen Namen (Vor- und Nachname oder Firmenname) hat.`,
		});
	}
	if (street && !isStrtNmOrAdrLine1Valid(street)) {
		problems.push({
			field: "street",
			message: `Strasse oder Adresszeile 1 des endgültigen Zahlungspflichtigen (EZP_STRASSE_ODER_ADRESSZEILE_1) ist unzulässig (${street})`,
		});
	}
	if (!isPstCdValid(postalCode, ADDRESS_TYPE)) {
		problems.push({
			field: "postal_code",
			message: `Postleitzahl des endgültigen Zahlungspflichtigen (EZP_POSTLEITZAHL) ist unzulässig (${ADDRESS_TYPE}, ${postalCode})`,
		});
	}
	if (!isTwnNmValid(city, ADDRESS_TYPE)) {
		problems.push({
			field: "city",
			message: `Ort des endgültigen Zahlungspflichtigen (EZP_ORT) ist unzulässig (${ADDRESS_TYPE}, ${city})`,
		});
	}
	if (!isCtryValid(country)) {
		problems.push({
			field: "country",
			message: `Land des endgültigen Zahlungspflichtigen (EZP_LAND) ist unzulässig (${country})`,
		});
	}
	return problems;
};

/** Every creditor (bank account) problem of the QR bill, in the order the QR slip reports them. */
export const validatePaymentSlipCreditor = (
	bankAccount: BankAccountEntityType
): string[] => {
	const missing: [unknown, string][] = [
		[
			bankAccount.reference_number,
			"Dem gewählten Bankkonto ist keine Referenznummer zugeordnet. Wenn Sie keine Referenznummer haben, setzen Sie sie auf 0000000000000.",
		],
		[
			bankAccount.qr_iban,
			"Dem gewählten Bankkonto ist keine IBAN zugeordnet.",
		],
		[
			bankAccount.cdtr_name,
			"Dem gewählten Bankkonto ist kein Name zugeordnet.",
		],
		[
			bankAccount.cdtr_adrtp,
			"Dem gewählten Bankkonto ist kein Adresstyp zugeordnet.",
		],
		[
			bankAccount.cdtr_street_line_1,
			"Dem gewählten Bankkonto ist keine Strasse zugeordnet.",
		],
		[
			bankAccount.cdtr_house_number_line_2,
			"Dem gewählten Bankkonto ist keine Hausnummer zugeordnet.",
		],
		[
			bankAccount.cdtr_area_code,
			"Dem gewählten Bankkonto ist keine Postleitzahl zugeordnet.",
		],
		[
			bankAccount.cdtr_city,
			"Dem gewählten Bankkonto ist keine Stadt zugeordnet.",
		],
	];
	const problems = missing
		.filter(([value]) => !value)
		.map(([, message]) => message);
	if (problems.length > 0) return problems;

	if (
		!isRefValid(
			generateQrReferenceNumber(
				bankAccount.reference_number ?? "",
				SAMPLE_INVOICE_NUMBER
			)
		)
	) {
		problems.push(
			`Die Referenznummer des Bankkontos ist für den QR-Einzahlungsschein ungültig (${bankAccount.reference_number}). Sie darf nur aus höchstens 13 Ziffern bestehen; RF-Referenzen werden nicht unterstützt.`
		);
	}

	// All present after the check above.
	const qr_iban = bankAccount.qr_iban ?? "";
	const cdtr_name = bankAccount.cdtr_name ?? "";
	const cdtr_street_line_1 = bankAccount.cdtr_street_line_1 ?? "";
	const cdtr_house_number_line_2 = bankAccount.cdtr_house_number_line_2 ?? "";
	const cdtr_area_code = bankAccount.cdtr_area_code ?? "";
	const cdtr_city = bankAccount.cdtr_city ?? "";
	const country = bankAccount.cdtr_country?.toUpperCase() || "CH";

	if (!isIBANValid(qr_iban)) {
		problems.push(
			`IBAN des Zahlungsempfängers (KONTO) ist unzulässig (${qr_iban})`
		);
	}
	if (!isNameValid(cdtr_name)) {
		problems.push(
			`Name des Zahlungsempfängers (ZE_NAME) ist unzulässig (${cdtr_name})`
		);
	}
	if (!isStrtNmOrAdrLine1Valid(cdtr_street_line_1)) {
		problems.push(
			`Strasse oder Adresszeile 1 des Zahlungsempfängers (ZE_STRASSE_ODER_ADRESSZEILE_1) ist unzulässig (${cdtr_street_line_1})`
		);
	}
	if (!isBldgNbOrAdrLine2Valid(cdtr_house_number_line_2, ADDRESS_TYPE)) {
		problems.push(
			`Hausnummer oder Adresszeile 2 des Zahlungsempfängers (ZE_HAUSNUMMER_ODER_ADRESSZEILE_2) ist unzulässig (${ADDRESS_TYPE}, ${cdtr_house_number_line_2})`
		);
	}
	if (!isPstCdValid(cdtr_area_code, ADDRESS_TYPE)) {
		problems.push(
			`Die Postleitzahl des Zahlungsempfängers ist unzulässig (ZE_POSTLEITZAHL lautet "${cdtr_area_code}", ZE_ADRESS_TYP lautet "${ADDRESS_TYPE}"). Sie können diese in den Einstellungen des Bankkontos ändern.`
		);
	}
	if (!isTwnNmValid(cdtr_city, ADDRESS_TYPE)) {
		problems.push(
			`Ort des Zahlungsempfängers (ZE_ORT) ist unzulässig (${ADDRESS_TYPE}, ${cdtr_city})`
		);
	}
	if (!isCtryValid(country)) {
		problems.push(
			`Land des Zahlungsempfängers (ZE_LAND) ist unzulässig (${country})`
		);
	}
	return problems;
};
